import { forwardRef, memo } from "react";

import { cn } from "@oneleet/utils";

import * as Checkbox from "@radix-ui/react-checkbox";
import { VariantProps, cva } from "class-variance-authority";

const DEFAULT_SIZE = "md";
const DEFAULT_DISABLED = false;

export type { CheckedState } from "@radix-ui/react-checkbox";

const checkboxContainer = cva(
  "border-neutral-250 radix-state-checked:border-accent-800 radix-state-unchecked:bg-white radix-state-checked:[&_svg]:size-[9px] bg-accent-800 radix-state-indeterminate:border-accent-800 radix-state-indeterminate:text-white radix-disabled:!bg-neutral-250 radix-disabled:!border-neutral-250 group flex shrink-0 items-center justify-center border border-[1.5px] transition-shadow duration-100 hover:border-neutral-300 focus:shadow-[0px_0px_0px_1px_theme(colors.accent.800),_0px_0px_0px_4px_theme(colors.accent.200)]",
  {
    variants: {
      size: {
        sm: "size-3.5 rounded",
        md: "size-4 rounded-[5px]",
        lg: "size-5 rounded-[5px]",
      },
      disabled: {
        true: "border-neutral-250 bg-neutral-250 cursor-not-allowed shadow-none",
        false: "",
      },
    },
    defaultVariants: {
      size: DEFAULT_SIZE,
      disabled: DEFAULT_DISABLED,
    },
  },
);

const checkboxIndicator = cva(
  "radix-state-checked:text-white radix-state-checked:radix-disabled:text-neutral-600 overflow-hidden p-0",
  {
    variants: {
      size: {
        sm: "[&>svg]:w-[8px]",
        md: "[&>svg]:w-[9px]",
        lg: "[&>svg]:w-[11px]",
      },
      disabled: {
        true: "bg-neutral-250",
        false: "",
      },
    },
    defaultVariants: {
      size: DEFAULT_SIZE,
      disabled: DEFAULT_DISABLED,
    },
  },
);

// Using a memoized wrapper for the root avoids an infinite loop crash when using an `onClick` handler on a parent div of an `NgCheckbox` inside a form
// See https://github.com/radix-ui/primitives/issues/2549 and https://github.com/radix-ui/primitives/issues/3192
function CheckboxRootWrapper(
  props: React.ComponentProps<typeof Checkbox.Root>,
) {
  const MemoizedRoot = memo(Checkbox.Root);
  return <MemoizedRoot {...props} />;
}

const NgCheckbox = forwardRef<
  React.ElementRef<typeof Checkbox.Root>,
  Omit<React.ComponentPropsWithoutRef<typeof Checkbox.Root>, "className"> &
    VariantProps<typeof checkboxContainer> & {
      classNames?: {
        container?: string;
        indicator?: string;
      };
    }
>(({ classNames, size = DEFAULT_SIZE, ...props }, ref) => (
  <CheckboxRootWrapper
    ref={ref}
    className={cn(
      checkboxContainer({
        disabled: props.disabled,
        size,
      }),
      classNames?.container,
    )}
    {...props}
  >
    <Checkbox.Indicator
      className={cn(checkboxIndicator({ size }), classNames?.indicator)}
    >
      <>
        <svg
          viewBox="0 0 12 10"
          className="group-radix-state-checked:block hidden fill-current"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.142 1.28376C11.6193 1.76107 11.6193 2.53494 11.142 3.01225L5.43832 8.71595C4.96101 9.19326 4.18714 9.19326 3.70983 8.71595L0.857981 5.8641C0.380673 5.38679 0.380673 4.61292 0.857981 4.13561C1.33529 3.65831 2.10916 3.65831 2.58646 4.13561L4.57407 6.12322L9.41354 1.28376C9.89084 0.806454 10.6647 0.806454 11.142 1.28376Z"
          />
        </svg>
        <svg
          viewBox="0 0 10 2"
          className="group-radix-state-indeterminate:block hidden stroke-current"
        >
          <line
            x1="1"
            y1="1"
            x2="9"
            y2="1"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </>
    </Checkbox.Indicator>
  </CheckboxRootWrapper>
));
NgCheckbox.displayName = Checkbox.Root.displayName;

export default NgCheckbox;
